import React, { useEffect, useState } from 'react';

const Step2ConfigurationList = ({ wallWidth, sinkCount, onSelectConfiguration, onBack }) => {
  const [configurations, setConfigurations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [thumbnails, setThumbnails] = useState({});

  const apiBase = 'https://hb.g-b.in/wp-json/wp/v2';

  useEffect(() => {
    const fetchConfigurations = async () => {
      try {
        const res = await fetch(`${apiBase}/configuration?per_page=100&_embed`);
        const allConfigs = await res.json();

        const minWidth = Number(wallWidth) - 3;
        const maxWidth = Number(wallWidth);

        const matched = allConfigs.filter((config) => {
          const width = parseFloat(config.acf?.total_width);
          const sinkClass = config.class_list.find((cls) => cls.startsWith('sinks-'));
          const sinkCountStr = sinkClass?.replace('sinks-', '').replace('-sinks', '').replace('-sink', '');
          const configSinks = parseInt(sinkCountStr, 10);
          return Number.isFinite(width) && width >= minWidth && width <= maxWidth && configSinks === sinkCount;
        });

        setConfigurations(matched);
        setLoading(false);

        fetchThumbnails(matched);
      } catch (err) {
        console.error('Error fetching configurations:', err);
        setLoading(false);
      }
    };

    const fetchThumbnails = async (matchedConfigs) => {
      const result = {};
      await Promise.all(
        matchedConfigs.map(async (config) => {
          result[config.id] = [];

          const variantId = config.variant?.[0];
          const products = config.acf?.products || [];
          if (!variantId || products.length === 0) return;

          for (let i = 0; i < products.length; i++) {
            const productId = products[i].product;
            try {
              const productRes = await fetch(`${apiBase}/product/${productId}`);
              const product = await productRes.json();
              const baseModel = product.title.rendered.split('-')[0];

              const variantRes = await fetch(`${apiBase}/variant/${variantId}`);
              const variant = await variantRes.json();
              const slug = `${baseModel}-${variant.slug?.toUpperCase()}`;

              const altRes = await fetch(`${apiBase}/product?slug=${slug}&_embed`);
              const altData = await altRes.json();
              const media = altData?.[0]?._embedded?.['wp:featuredmedia']?.[0];

              const imgUrl =
                media?.media_details?.sizes?.medium?.source_url ||
                media?.media_details?.sizes?.large?.source_url ||
                media?.media_details?.sizes?.full?.source_url ||
                media?.source_url ||
                'https://via.placeholder.com/300x200?text=No+Image';

              const widthTerm =
                altData?.[0]?._embedded?.['wp:term']?.flat().find((term) => term.taxonomy === 'width')?.name || 'N/A';

              result[config.id].push({ url: imgUrl, width: widthTerm });
            } catch (e) {
              console.warn(`Thumbnail not found for product ${productId} in config ${config.id}`);
            }
          }
        })
      );

      setThumbnails(result);
    };

    fetchConfigurations();
  }, [wallWidth, sinkCount]);

  if (loading) {
    return (
      <div className="text-center mt-5">
        <div className="spinner-border text-primary" role="status" />
        <div className="mt-3">Loading configurations...</div>
      </div>
    );
  }

  const minDisplay = Number(wallWidth) - 3;

  return (
    <div className="pb-3">
      <div className="section-heading">
        <h2>
          {minDisplay}"
          {' - '}
          {wallWidth}" vanity with {sinkCount} sink{sinkCount > 1 ? 's' : ''}
        </h2>
        <p>
          Select the configuration your preferred configuration.
          <br />
          <i>
            Note: If your exact vanity width is not available, fillers can be added between cabinets to fill the extra space.
          </i>
        </p>
      </div>

      <div className="fixed-footer">
        <div className="text-start justify-center text-center">
          <button className="button back" onClick={onBack}>
            ← Back
          </button>
        </div>
      </div>

      {configurations.length === 0 ? (
        <div className="alert alert-warning text-center">
          No matching configurations found. Try adjusting your wall width or sink count.
        </div>
      ) : (
        <div className="configurations-grid">
          {configurations.map((config) => {
            const cfgWidth = parseFloat(config.acf?.total_width) || 0;
            const needsFillers = Number(wallWidth) > cfgWidth; // user width larger than config width

            return (
              <div className="configuration-card" key={config.id}>
                {Array.isArray(thumbnails[config.id]) && thumbnails[config.id].length > 0 && (
                  <div className="product-thumbnails">
                    {thumbnails[config.id].map((thumb, idx) => (
                      <div key={idx} className="product-thumbnail">
                        <img src={thumb.url} alt={`Product ${idx + 1}`} />
                      </div>
                    ))}
                  </div>
                )}

                <div>
                  <p className="mb-1">
                    <strong>Total Width:</strong> {cfgWidth}"
                  </p>
                  <p className="mb-2">
                    <strong>Sinks:</strong>{' '}
                    {config.class_list
                      .find((cls) => cls.startsWith('sinks-'))
                      ?.replace('sinks-', '')
                      .replace('-sinks', '')
                      .replace('-sink', '')}
                  </p>

                  {needsFillers && (
                    <p className="small text-alert mb-3">
                      <i><strong>Note:</strong> additional fillers between cabinets are needed to fill the space.</i>
                    </p>
                  )}

                  <button className="select-config button select" onClick={() => onSelectConfiguration(config)}>
                    Select Configuration →
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Step2ConfigurationList;
